import React from 'react';
import { Check, X, Vote, Coins, Crown, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const T = {
  de: {
    eyebrow: 'Zwei Aktiengattungen',
    title: 'Stamm- oder Vorzugsaktie?',
    intro: 'Wählen Sie die Aktiengattung, die zu Ihren Zielen passt – mehr Mitbestimmung oder mehr Dividende.',
    namen: {
      tag: 'Verfügbar',
      title: 'Namen­aktie',
      subtitle: 'Mitbestimmung & solide Basisdividende',
      dividend: 'Zieldividende 4–6% p.a.',
      voting: 'Volles Stimm­recht an der General­versammlung',
      priority: 'Dividende nach Vorzugs­aktien­bedienung',
      features: ['Volle Mitwirkung an strategischen Entscheidungen', 'Eintragung als Aktionär im Schweizer Aktien­register', 'Übertragbar & vererbbar'],
    },
    vorzugs: {
      tag: 'Geplant',
      title: 'Vorzugs­aktie',
      subtitle: 'Bevorzugte Dividenden­ausschüttung',
      dividend: 'Zieldividende 8–10% p.a.',
      voting: 'Kein Stimm­recht (rein dividenden­orientiert)',
      priority: 'Dividende wird bevorzugt ausgeschüttet',
      features: ['Höhere Ausschüttungs­erwartung', 'Erstrangige Dividenden­bedienung gemäss Statuten', 'Übertragbar & vererbbar'],
    },
    disclaimer: 'Alle Zieldividenden sind Plan­werte und nicht garantiert. Die tatsächliche Auszahlung hängt vom Geschäftsverlauf und vom Beschluss der General­versammlung ab.',
  },
  en: {
    eyebrow: 'Two share classes',
    title: 'Ordinary or preferred share?',
    intro: 'Choose the share class that matches your goals – more voting power or more dividend.',
    namen: {
      tag: 'Available',
      title: 'Ordinary share',
      subtitle: 'Voting rights & solid base dividend',
      dividend: 'Target dividend 4–6% p.a.',
      voting: 'Full voting right at the general meeting',
      priority: 'Dividend after preferred shareholders are paid',
      features: ['Full participation in strategic decisions', 'Registered in the official Swiss share register', 'Transferable & inheritable'],
    },
    vorzugs: {
      tag: 'Planned',
      title: 'Preferred share',
      subtitle: 'Preferred dividend payout',
      dividend: 'Target dividend 8–10% p.a.',
      voting: 'No voting right (dividend-focused)',
      priority: 'Dividend is paid out with priority',
      features: ['Higher expected payout', 'Senior dividend treatment per articles of association', 'Transferable & inheritable'],
    },
    disclaimer: 'All target dividends are plan values, not guaranteed. The actual payout depends on business performance and the resolution of the general meeting.',
  },
  ru: {
    eyebrow: 'Два класса акций',
    title: 'Обыкновенные или привилегированные акции?',
    intro: 'Выберите класс акций, который соответствует вашим целям — больше прав голоса или больше дивидендов.',
    namen: {
      tag: 'Доступно',
      title: 'Обыкновенные акции',
      subtitle: 'Права голоса и стабильная базовая дивиденда',
      dividend: 'Целевая дивиденда 4–6% годовых',
      voting: 'Полное право голоса на общем собрании',
      priority: 'Дивиденда после выплаты привилегированным акционерам',
      features: ['Полное участие в стратегических решениях', 'Регистрация в швейцарском реестре акций', 'Передаваемы и наследуемы'],
    },
    vorzugs: {
      tag: 'Планируется',
      title: 'Привилегированные акции',
      subtitle: 'Приоритетная выплата дивидендов',
      dividend: 'Целевая дивиденда 8–10% годовых',
      voting: 'Без права голоса (ориентир — дивиденда)',
      priority: 'Дивиденда выплачивается в первую очередь',
      features: ['Более высокий ожидаемый доход', 'Старшая очередь по дивидендам согласно уставу', 'Передаваемы и наследуемы'],
    },
    disclaimer: 'Все целевые дивиденды являются плановыми значениями, не гарантированы. Фактическая выплата зависит от результатов бизнеса и решения общего собрания.',
  },
  vi: {
    eyebrow: 'Hai loại cổ phiếu',
    title: 'Cổ phiếu phổ thông hay ưu đãi?',
    intro: 'Chọn loại cổ phiếu phù hợp với mục tiêu của bạn – nhiều quyền biểu quyết hơn hoặc nhiều cổ tức hơn.',
    namen: {
      tag: 'Có sẵn',
      title: 'Cổ phiếu phổ thông',
      subtitle: 'Quyền biểu quyết & cổ tức cơ bản ổn định',
      dividend: 'Cổ tức mục tiêu 4–6%/năm',
      voting: 'Toàn quyền biểu quyết tại đại hội',
      priority: 'Cổ tức sau khi trả cho cổ đông ưu đãi',
      features: ['Tham gia đầy đủ vào quyết định chiến lược', 'Đăng ký trong sổ cổ đông Thụy Sĩ', 'Có thể chuyển nhượng & thừa kế'],
    },
    vorzugs: {
      tag: 'Dự kiến',
      title: 'Cổ phiếu ưu đãi',
      subtitle: 'Ưu tiên chi trả cổ tức',
      dividend: 'Cổ tức mục tiêu 8–10%/năm',
      voting: 'Không có quyền biểu quyết (tập trung vào cổ tức)',
      priority: 'Cổ tức được trả ưu tiên',
      features: ['Kỳ vọng cổ tức cao hơn', 'Ưu tiên cao theo điều lệ công ty', 'Có thể chuyển nhượng & thừa kế'],
    },
    disclaimer: 'Tất cả các mức cổ tức mục tiêu là giá trị kế hoạch, không được bảo đảm. Việc chi trả thực tế phụ thuộc vào kết quả kinh doanh và quyết định của đại hội cổ đông.',
  },
};

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-white/5 last:border-0">
    <Icon className="h-4 w-4 text-[#C4A55A] mt-0.5 shrink-0" />
    <div>
      <div className="text-xs uppercase tracking-wider text-gray-400 mb-0.5">{label}</div>
      <div className="text-sm text-white leading-snug">{value}</div>
    </div>
  </div>
);

const Card = ({ data, accent, testId, votingPositive }) => (
  <div
    data-testid={testId}
    className={`relative bg-[#1a1a1a] border ${accent ? 'border-[#C4A55A]/55' : 'border-[#C4A55A]/20'} rounded-2xl p-6 md:p-7 transition-all duration-300 hover:border-[#C4A55A]/60`}
  >
    <span className={`absolute top-4 right-4 px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full ${
      data.tag === 'Geplant' || data.tag === 'Planned' || data.tag === 'Планируется' || data.tag === 'Dự kiến'
        ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
        : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
    }`}>
      {data.tag}
    </span>

    <div className="flex items-center gap-3 mb-4">
      <div className="w-11 h-11 rounded-xl bg-[#C4A55A]/15 border border-[#C4A55A]/40 flex items-center justify-center">
        {accent ? <Crown className="h-5 w-5 text-[#C4A55A]" /> : <ShieldCheck className="h-5 w-5 text-[#C4A55A]" />}
      </div>
      <div>
        <h3 className="text-xl font-bold text-white">{data.title}</h3>
        <p className="text-sm text-gray-400">{data.subtitle}</p>
      </div>
    </div>

    <Row icon={Coins} label="Dividende" value={data.dividend} />
    <Row icon={Vote} label="Stimmrecht" value={
      <span className="inline-flex items-center gap-1.5">
        {votingPositive ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <X className="h-3.5 w-3.5 text-gray-500" />}
        {data.voting}
      </span>
    } />
    <Row icon={Crown} label="Reihenfolge" value={data.priority} />

    <ul className="mt-4 space-y-1.5">
      {data.features.map((f, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
          <Check className="h-4 w-4 text-[#C4A55A] mt-0.5 shrink-0" />
          {f}
        </li>
      ))}
    </ul>
  </div>
);

export const Aktiengattungen = () => {
  const { language } = useLanguage();
  const t = T[language] || T.de;

  return (
    <section id="aktiengattungen" className="py-16 bg-[#0a0a0a]">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#C4A55A]/15 border border-[#C4A55A]/30 text-[#C4A55A] text-xs uppercase tracking-wider font-semibold mb-3">
            {t.eyebrow}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">{t.title}</h2>
          <p className="text-gray-300">{t.intro}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Card data={t.namen}   testId="card-stamm"   accent={false} votingPositive={true} />
          <Card data={t.vorzugs} testId="card-vorzugs" accent={true}  votingPositive={false} />
        </div>

        <p className="text-xs text-gray-500 text-center mt-6 max-w-3xl mx-auto leading-relaxed">
          {t.disclaimer}
        </p>
      </div>
    </section>
  );
};
